/**
 * 22 Oct 2026 (D-1022-COMPANION): the buttons of a "No volunteer yet" item — Widen to everyone (only when the request
 * allows it; a person's press, queued through the cancel window), Call the tenant (the number is never printed), and
 * Open (the request; with proposals waiting it opens them). The item itself sends nothing.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RequestView, SendResult } from '@shared/volunteers-api';
import { formatDayKey } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { CallButton } from '@/components/Contact';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { postJson, useVolunteerRefresh } from './api';

export function NoVolunteerButtons({ request, onOpen }: { request: RequestView | undefined; onOpen: (r: RequestView, el: HTMLElement) => void }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const [busy, setBusy] = useState(false);
  if (!request) return null;
  const day = formatDayKey(request.dayKey, i18n.language);
  const proposals = (request.proposals ?? []).length;
  const widen = async () => {
    setBusy(true);
    try {
      const res = await postJson<{ send: SendResult }>(`/api/volunteers/requests/${request.requestId}`, { what: 'widen' });
      toast(res.send.message);
      announce(res.send.message);
      refresh();
    } catch (e) {
      const m = errorText(e);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="book183-row-actions" data-testid={`nv-actions-${request.requestId}`}>
      {request.widen ? (
        <Button size="sm" disabled={busy} aria-label={t('vol.noVol.widenAria', { unit: request.tenant.unit, day })} onClick={() => widen()} data-testid={`nv-widen-${request.requestId}`}>
          <Icon name="mail" /> {t('vol.noVol.widen')}
        </Button>
      ) : null}
      {request.tenant.phone ? <CallButton phone={request.tenant.phone} name={`${request.tenant.firstName} (${request.tenant.unit})`} className="" /> : null}
      <Button variant="secondary" size="sm" aria-label={t('vol.noVol.openAria', { unit: request.tenant.unit, day })} onClick={(e) => onOpen(request, e.currentTarget)} data-testid={`nv-open-${request.requestId}`}>
        {proposals ? t('vol.noVol.openProposals') : t('vol.noVol.open')}
      </Button>
    </div>
  );
}
